import { useCallback, useEffect, useState } from 'react'
import { assistantConfig } from '@adapters/ai/config.ts'
import {
  guardarAvisos,
  suscripcionActual,
  type Aviso,
  type AvisosFailure,
} from '@adapters/push/avisos.ts'
import { es } from '../strings/es.ts'
import { CheckRow, type CheckTone } from './CheckRow.tsx'

/**
 * La prueba de avisos, para hacerla en la tablet de verdad.
 *
 * Existe porque la pregunta que decide el resto no se puede contestar desde un
 * portátil: si la app puede llamar a un niño a las nueve de la noche **con la
 * app cerrada** depende de esa tablet, de su Android y de lo que el fabricante
 * haya decidido silenciar. Comprobarlo en un Chrome de escritorio no dice nada.
 *
 * Hay tres caminos posibles y esta pantalla dice cuál está abierto:
 *
 *   1. `TimestampTrigger` — avisos programados por el propio dispositivo, sin
 *      servidor y sin red. Sería el ideal: funciona en modo avión. Nunca llegó
 *      a salir de experimental en Chrome, así que lo más probable es que no
 *      esté, pero comprobarlo es gratis y cambiaría el diseño entero.
 *   2. `PushManager` — el aviso lo manda un servidor. Funciona con la app
 *      cerrada, pero **necesita que la tablet tenga red en ese momento**, y en
 *      Android pasa por la infraestructura de Google. Es el camino realista.
 *   3. Nada de lo anterior — hace falta empaquetar la app para Android y usar
 *      una alarma del sistema. Es lo más fiable y lo más trabajo.
 *
 * Y una comprobación que no es teórica: que un aviso **se vea**. Tener el
 * permiso concedido no basta; el sistema, el modo «no molestar» o los ajustes
 * del fabricante pueden tragárselo sin decir nada.
 */

interface Soporte {
  readonly notificaciones: boolean
  readonly permiso: NotificationPermission | 'no-disponible'
  readonly worker: boolean
  readonly push: boolean
  readonly programables: boolean
  readonly periodico: boolean
}

async function mirar(): Promise<Soporte> {
  const notificaciones = 'Notification' in window
  const registro =
    'serviceWorker' in navigator
      ? await navigator.serviceWorker.getRegistration().catch(() => undefined)
      : undefined

  return {
    notificaciones,
    permiso: notificaciones ? Notification.permission : 'no-disponible',
    worker: registro !== undefined,
    push: registro !== undefined && 'pushManager' in registro,
    // Si esto existiera, el dispositivo podría avisar solo y sin red.
    programables: 'showTrigger' in Notification.prototype && 'TimestampTrigger' in window,
    periodico: registro !== undefined && 'periodicSync' in registro,
  }
}

export function NotificationsPanel({ avisos }: { avisos: readonly Aviso[] }) {
  const [soporte, setSoporte] = useState<Soporte | null>(null)
  const [nota, setNota] = useState<string | null>(null)
  const [probando, setProbando] = useState(false)
  // Las horas ya no se ponen aquí: son un acuerdo de la casa y viven en el
  // registro, en los ajustes de cada hijo. Esta pantalla solo apunta la tablet
  // para que además llegue el golpe en la puerta, que es la copia de fuera.
  const [suscrito, setSuscrito] = useState(false)
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    let cancelled = false
    void suscripcionActual()
      .then((sub) => {
        if (!cancelled) setSuscrito(sub !== null)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const cargar = useCallback(() => {
    void mirar()
      .then(setSoporte)
      .catch(() => {
        setSoporte(null)
      })
  }, [])

  useEffect(cargar, [cargar])

  const guardar = async (): Promise<void> => {
    setGuardando(true)
    setNota(null)
    try {
      const config = assistantConfig()
      const result = await guardarAvisos(config.url, config.token, avisos)
      if (result.ok) {
        setNota(es.avisos.guardado(result.avisos))
        setSuscrito(result.avisos > 0)
        cargar()
      } else {
        setNota(es.avisos.fallos[result.failure satisfies AvisosFailure])
      }
    } finally {
      setGuardando(false)
    }
  }

  const probar = async (): Promise<void> => {
    setProbando(true)
    setNota(null)
    try {
      if (!('Notification' in window)) {
        setNota(es.avisos.sinSoporte)
        return
      }
      const permiso = await Notification.requestPermission()
      cargar()
      if (permiso !== 'granted') {
        setNota(es.avisos.denegado)
        return
      }
      const registro = await navigator.serviceWorker.getRegistration()
      if (!registro) {
        setNota(es.avisos.sinWorker)
        return
      }
      // Desde el registro y no desde la página: en Android un aviso de la
      // página no sobrevive a cerrar la pestaña, y lo que se quiere probar es
      // justo el que sí.
      await registro.showNotification(es.avisos.pruebaTitulo, {
        body: es.avisos.pruebaCuerpo,
        icon: '/icons/icon-192.png',
        tag: 'prueba',
      })
      setNota(es.avisos.mandado)
    } catch (cause) {
      console.error('No se pudo probar el aviso', cause)
      setNota(es.avisos.fallo)
    } finally {
      setProbando(false)
    }
  }

  const programar = async (): Promise<void> => {
    setProbando(true)
    setNota(null)
    try {
      const registro = await navigator.serviceWorker.getRegistration()
      if (!registro) {
        setNota(es.avisos.sinWorker)
        return
      }
      const conTrigger = window as unknown as {
        TimestampTrigger: new (t: number) => unknown
      }
      await registro.showNotification(es.avisos.pruebaTitulo, {
        body: es.avisos.programadoCuerpo,
        icon: '/icons/icon-192.png',
        tag: 'programada',
        // No está en los tipos porque no está en la especificación estable.
        showTrigger: new conTrigger.TimestampTrigger(Date.now() + 60_000),
      } as NotificationOptions)
      setNota(es.avisos.programado)
    } catch (cause) {
      console.error('No se pudo programar el aviso', cause)
      setNota(es.avisos.fallo)
    } finally {
      setProbando(false)
    }
  }

  if (!soporte) return null

  const tonoPermiso: CheckTone =
    soporte.permiso === 'granted' ? 'ok' : soporte.permiso === 'denied' ? 'warn' : 'info'

  /** Qué camino queda abierto, dicho sin rodeos. */
  const veredicto = soporte.programables
    ? { texto: es.avisos.caminoLocal, tono: 'ok' as CheckTone, pista: es.avisos.caminoLocalPista }
    : soporte.push
      ? { texto: es.avisos.caminoPush, tono: 'info' as CheckTone, pista: es.avisos.caminoPushPista }
      : {
          texto: es.avisos.caminoNativo,
          tono: 'warn' as CheckTone,
          pista: es.avisos.caminoNativoPista,
        }

  return (
    <section className="mt-10">
      <h2 className="mb-1 text-lg font-extrabold">{es.avisos.title}</h2>
      <p className="text-muted mt-0 mb-1 text-sm">{es.avisos.intro}</p>
      <p className="text-muted mt-0 mb-4 text-sm">{es.avisos.intro2}</p>

      <CheckRow
        label={es.avisos.permiso}
        value={es.avisos.permisos[soporte.permiso]}
        tone={tonoPermiso}
      />
      {soporte.worker ? (
        <CheckRow label={es.avisos.worker} value={es.common.yes} tone="ok" />
      ) : (
        <CheckRow
          label={es.avisos.worker}
          value={es.common.no}
          tone="warn"
          hint={es.avisos.sinWorkerPista}
        />
      )}
      <CheckRow
        label={es.avisos.programables}
        value={soporte.programables ? es.common.yes : es.common.no}
        tone={soporte.programables ? 'ok' : 'info'}
        hint={es.avisos.programablesPista}
      />
      <CheckRow
        label={es.avisos.push}
        value={soporte.push ? es.common.yes : es.common.no}
        tone={soporte.push ? 'ok' : 'warn'}
        hint={es.avisos.pushPista}
      />
      <CheckRow
        label={es.avisos.suscrita}
        value={suscrito ? es.common.yes : es.common.no}
        tone={suscrito ? 'ok' : 'info'}
      />
      <CheckRow
        label={es.avisos.veredicto}
        value={veredicto.texto}
        tone={veredicto.tono}
        hint={veredicto.pista}
      />

      {soporte.push ? (
        <section className="border-rule mt-6 border-t pt-5">
          <h3 className="m-0 mb-1 text-base font-extrabold">{es.avisos.horas}</h3>
          <p className="text-muted mt-0 mb-1 text-sm">{es.avisos.horasHelp}</p>
          <p className="text-coral mt-0 mb-4 text-sm font-bold">{es.avisos.necesitaRed}</p>

          {avisos.length === 0 ? (
            <p className="text-muted m-0 text-sm font-bold">{es.avisos.sinHoras}</p>
          ) : (
            <button
              type="button"
              disabled={guardando}
              onClick={() => {
                void guardar()
              }}
              className="bg-teal rounded-lg px-5 py-3 font-bold text-white disabled:opacity-40"
            >
              {es.avisos.apuntar}
            </button>
          )}
        </section>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={probando}
          onClick={() => {
            void probar()
          }}
          className="bg-teal rounded-lg px-5 py-3 font-bold text-white disabled:opacity-40"
        >
          {es.avisos.probar}
        </button>
        {soporte.programables ? (
          <button
            type="button"
            disabled={probando}
            onClick={() => {
              void programar()
            }}
            className="border-rule rounded-lg border-2 px-5 py-3 font-bold disabled:opacity-40"
          >
            {es.avisos.probarProgramado}
          </button>
        ) : null}
      </div>

      {nota ? (
        <p role="status" className="text-muted mt-3 text-sm font-bold">
          {nota}
        </p>
      ) : null}
    </section>
  )
}
